import type { UploadTarget } from "@esi/shared";

/** Direct object transport shared by file uploads. No API fetch, credentials or extra headers. */
export function putStorageFile(
  upload: UploadTarget,
  file: File,
  signal?: AbortSignal,
  progress?: (sent: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const settle = (fn: () => void) => {
      signal?.removeEventListener("abort", abort);
      fn();
    };
    xhr.open(upload.method, upload.url);
    xhr.withCredentials = false;
    for (const [name, value] of Object.entries(upload.headers)) {
      // The browser derives Content-Length from the File, and forbids setting it in script.
      if (name.toLowerCase() !== "content-length") {
        xhr.setRequestHeader(name, value);
      }
    }
    xhr.upload.onprogress = (e) => progress?.(e.loaded, e.lengthComputable ? e.total : file.size);
    xhr.onload = () =>
      settle(() => {
        // A create-only URL's 412 means an earlier PUT stored this exact intent's object.
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 412) {
          progress?.(file.size, file.size);
          resolve();
        } else {
          reject(new Error("storage_put"));
        }
      });
    xhr.onerror = () => settle(() => reject(new Error("storage_network")));
    xhr.ontimeout = xhr.onerror;
    xhr.onabort = () => settle(() => reject(new DOMException("Aborted", "AbortError")));
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) {
      settle(() => reject(new DOMException("Aborted", "AbortError")));
      return;
    }
    xhr.send(file);
  });
}
